import { doc, runTransaction, collection, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { trackError } from './errorTracking';

// Flag global pour éviter le double tracking
let isTracking = false;

// Fonction pour formater les dates
const formatDate = (date) => ({
  dateStr: date.toISOString().split('T')[0],
  monthStr: date.toISOString().split('T')[0].substring(0, 7),
  hourStr: date.getHours().toString().padStart(2, '0')
});

// Fonction helper : sanitizeFirestoreKey
const sanitizeFirestoreKey = (key) => {
  // Remplacer les caractères interdits par des underscores
  return key.replace(/[~*\/\[\]]/g, '_');
};

// Tracking des visites
export const trackAppVisit = async (isStandalone) => {
  if (!isStandalone || isTracking) return;
  isTracking = true;

  try {
    const today = new Date();
    const { dateStr, monthStr, hourStr } = formatDate(today);
    const visitsRef = doc(db, 'analytics', 'visits');

    await runTransaction(db, async (transaction) => {
      const visitsDoc = await transaction.get(visitsRef);
      if (!visitsDoc.exists()) {
        transaction.set(visitsRef, {
          totalVisits: 1,
          dailyVisits: { [dateStr]: 1 },
          monthlyVisits: { [monthStr]: 1 },
          hourlyVisits: { [dateStr]: { [hourStr]: 1 } }
        });
      } else {
        const data = visitsDoc.data();
        transaction.update(visitsRef, {
          totalVisits: (data.totalVisits || 0) + 1,
          [`dailyVisits.${dateStr}`]: ((data.dailyVisits || {})[dateStr] || 0) + 1,
          [`monthlyVisits.${monthStr}`]: ((data.monthlyVisits || {})[monthStr] || 0) + 1,
          [`hourlyVisits.${dateStr}.${hourStr}`]: (((data.hourlyVisits || {})[dateStr] || {})[hourStr] || 0) + 1
        });
      }
    });
  } catch (error) {
    console.error('❌ Erreur tracking visites:', error);
  } finally {
    isTracking = false;
  }
};

// Tracking des installations par boutique
export const trackStoreInstallation = async (storeId, storeName, device) => {
  try {
    const shopRef = doc(db, 'shopInstall', storeId);
    
    // Obtenir la date et le mois actuels
    const today = new Date();
    const dailyKey = today.toISOString().split('T')[0];  // Format: "2024-03-19"
    const monthlyKey = dailyKey.substring(0, 7);  // Format: "2024-03"

    await runTransaction(db, async (transaction) => {
      const shopDoc = await transaction.get(shopRef);
      
      if (!shopDoc.exists()) {
        // Première installation pour cette boutique
        transaction.set(shopRef, {
          name: storeName,
          total: 0,
          byDevice: { [device]: 0 },
          dailyStats: {
            [dailyKey]: {
              total: 0,
              [device]: 0
            }
          },
          monthlyStats: {
            [monthlyKey]: {
              total: 0,
              [device]: 0
            }
          }
        });
      } else {
        const data = shopDoc.data();
        
        // Mise à jour des totaux
        const updates = {
          total: (data.total || 0) + 1,
          [`byDevice.${device}`]: ((data.byDevice || {})[device] || 0) + 1,
          
          // Mise à jour des stats journalières
          [`dailyStats.${dailyKey}.total`]: ((data.dailyStats || {})[dailyKey]?.total || 0) + 1,
          [`dailyStats.${dailyKey}.${device}`]: ((data.dailyStats || {})[dailyKey]?.[device] || 0) + 1,
          
          // Mise à jour des stats mensuelles
          [`monthlyStats.${monthlyKey}.total`]: ((data.monthlyStats || {})[monthlyKey]?.total || 0) + 1,
          [`monthlyStats.${monthlyKey}.${device}`]: ((data.monthlyStats || {})[monthlyKey]?.[device] || 0) + 1
        };

        transaction.update(shopRef, updates);
      }
    });
  } catch (error) {
    console.error('❌ Erreur tracking boutique:', error);
    await trackError({
      error,
      type: 'store_tracking',
      context: { storeId, storeName, device }
    });
  }
};

// Ajouter l'export de trackVisit
export const trackVisit = async (device) => {
  try {
    const visitsRef = doc(db, 'analytics', 'visits');
    const today = new Date().toISOString().split('T')[0];
    const month = today.substring(0, 7);
    const hour = new Date().getHours().toString().padStart(2, '0');

    await runTransaction(db, async (transaction) => {
      const doc = await transaction.get(visitsRef);
      const data = doc.data() || {};

      // Incrémenter total
      const totalVisits = (data.totalVisits || 0) + 1;

      // Incrémenter stats journalières
      const dailyVisits = data.dailyVisits || {};
      dailyVisits[today] = (dailyVisits[today] || 0) + 1;

      // Incrémenter stats mensuelles
      const monthlyVisits = data.monthlyVisits || {};
      monthlyVisits[month] = (monthlyVisits[month] || 0) + 1;

      // Incrémenter stats horaires
      const hourlyVisits = data.hourlyVisits || {};
      if (!hourlyVisits[today]) {
        hourlyVisits[today] = {};
      }
      hourlyVisits[today][hour] = (hourlyVisits[today][hour] || 0) + 1;

      transaction.set(visitsRef, {
        totalVisits,
        dailyVisits,
        monthlyVisits,
        hourlyVisits,
        lastUpdate: serverTimestamp()
      }, { merge: true });
    });
  } catch (error) {
    console.error('Erreur tracking visite:', error);
  }
};

// Tracking des réceptions de notifications (non-bloquant, ne casse jamais l'app)
export const trackNotificationReceived = async (notificationData = {}) => {
  // Vérifier que Firestore est disponible
  if (!db) {
    console.warn('⚠️ Firestore non disponible pour le tracking');
    return;
  }

  try {
    const documentId = localStorage.getItem('documentId');
    const oneSignalId = localStorage.getItem('oneSignalId') || notificationData.userId;
    const userAgent = navigator.userAgent;
    const device = /iPad|iPhone|iPod/.test(userAgent) ? 'iOS' : 'Android';
    
    const today = new Date();
    const { dateStr, monthStr, hourStr } = formatDate(today);
    
    // Document pour tracker les réceptions de notifications
    const notificationsRef = doc(db, 'analytics', 'notifications');
    
    // Document individuel pour cette notification (si on a un ID)
    const notificationId = notificationData.id || `notif_${Date.now()}`;
    const notificationDocRef = doc(db, 'analytics/notifications/received', notificationId);

    // Essayer la transaction avec timeout implicite (Firestore gère l'offline)
    await Promise.race([
      runTransaction(db, async (transaction) => {
        // Mise à jour des stats globales
        const notificationsDoc = await transaction.get(notificationsRef);
        if (!notificationsDoc.exists()) {
          transaction.set(notificationsRef, {
            totalReceived: 1,
            dailyReceived: { [dateStr]: 1 },
            monthlyReceived: { [monthStr]: 1 },
            hourlyReceived: { [dateStr]: { [hourStr]: 1 } },
            byDevice: { [device]: 1 }
          });
        } else {
          const data = notificationsDoc.data();
          transaction.update(notificationsRef, {
            totalReceived: (data.totalReceived || 0) + 1,
            [`dailyReceived.${dateStr}`]: ((data.dailyReceived || {})[dateStr] || 0) + 1,
            [`monthlyReceived.${monthStr}`]: ((data.monthlyReceived || {})[monthStr] || 0) + 1,
            [`hourlyReceived.${dateStr}.${hourStr}`]: (((data.hourlyReceived || {})[dateStr] || {})[hourStr] || 0) + 1,
            [`byDevice.${device}`]: ((data.byDevice || {})[device] || 0) + 1
          });
        }
      }),
      // Timeout de sécurité (5 secondes max)
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Tracking timeout')), 5000)
      )
    ]).catch(error => {
      // Si offline ou timeout, on continue silencieusement
      if (error.message !== 'Tracking timeout') {
        console.warn('⚠️ Tracking notification (offline ou erreur):', error.message);
      }
    });

    // Enregistrer les détails de cette notification individuelle (non-bloquant)
    setDoc(notificationDocRef, {
      notificationId: notificationData.id || null,
      heading: notificationData.heading || null,
      content: notificationData.content || null,
      url: notificationData.url || null,
      icon: notificationData.icon || null,
      data: notificationData.data || null,
      documentId: documentId || null,
      oneSignalId: oneSignalId || null,
      device: device,
      userAgent: userAgent,
      receivedAt: serverTimestamp(),
      dateStr: dateStr,
      monthStr: monthStr,
      hourStr: hourStr
    }, { merge: true }).catch(error => {
      // Erreur silencieuse pour l'enregistrement individuel
      console.warn('⚠️ Erreur enregistrement notification individuelle:', error.message);
    });

  } catch (error) {
    // Toute erreur est silencieuse pour ne jamais gêner l'utilisateur
    console.warn('⚠️ Erreur tracking réception notification (non-bloquant):', error.message);
    // Ne pas tracker cette erreur pour éviter la récursion
  }
};

// Tracking des vues des nouveautés (News)
export const trackNewsView = async (newsId, newsTitle, newsCategory) => {
  try {
    const newsViewsRef = doc(db, 'analytics', 'newsViews');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedCategory = sanitizeFirestoreKey(newsCategory || 'unknown');
    const sanitizedNewsId = sanitizeFirestoreKey(newsId);

    await runTransaction(db, async (transaction) => {
      const viewsDoc = await transaction.get(newsViewsRef);
      
      if (!viewsDoc.exists()) {
        transaction.set(newsViewsRef, {
          total: 1,
          byCategory: { [sanitizedCategory]: 1 },
          byNews: {
            [sanitizedNewsId]: {
              title: newsTitle || 'Sans titre',
              views: 1
            }
          },
          dailyStats: {
            [dateStr]: {
              total: 1,
              [sanitizedCategory]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: 1,
              [sanitizedCategory]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = viewsDoc.data();
        const currentNewsData = (data.byNews || {})[sanitizedNewsId];
        const currentViews = currentNewsData?.views || 0;
        
        const updates = {
          total: (data.total || 0) + 1,
          [`byCategory.${sanitizedCategory}`]: ((data.byCategory || {})[sanitizedCategory] || 0) + 1,
          [`byNews.${sanitizedNewsId}`]: {
            title: newsTitle || 'Sans titre',
            views: currentViews + 1
          },
          [`dailyStats.${dateStr}.total`]: ((data.dailyStats || {})[dateStr]?.total || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedCategory}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedCategory] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: ((data.monthlyStats || {})[monthStr]?.total || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedCategory}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedCategory] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(newsViewsRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'news_view_tracking',
      context: { newsId, newsTitle, newsCategory }
    });
  }
};

// Tracking des clicks sur les nouveautés
export const trackNewsClick = async (newsId, newsTitle, newsCategory, newsUrl) => {
  try {
    const newsClicksRef = doc(db, 'analytics', 'newsClicks');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedCategory = sanitizeFirestoreKey(newsCategory || 'unknown');
    const sanitizedNewsId = sanitizeFirestoreKey(newsId);

    await runTransaction(db, async (transaction) => {
      const clicksDoc = await transaction.get(newsClicksRef);
      
      if (!clicksDoc.exists()) {
        transaction.set(newsClicksRef, {
          total: 1,
          byCategory: { [sanitizedCategory]: 1 },
          byNews: {
            [sanitizedNewsId]: {
              title: newsTitle || 'Sans titre',
              clicks: 1
            }
          },
          dailyStats: {
            [dateStr]: {
              total: 1,
              [sanitizedCategory]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: 1,
              [sanitizedCategory]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = clicksDoc.data();
        const currentNewsData = (data.byNews || {})[sanitizedNewsId];
        const currentClicks = currentNewsData?.clicks || 0;
        
        const updates = {
          total: (data.total || 0) + 1,
          [`byCategory.${sanitizedCategory}`]: ((data.byCategory || {})[sanitizedCategory] || 0) + 1,
          [`byNews.${sanitizedNewsId}`]: {
            title: newsTitle || 'Sans titre',
            clicks: currentClicks + 1
          },
          [`dailyStats.${dateStr}.total`]: ((data.dailyStats || {})[dateStr]?.total || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedCategory}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedCategory] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: ((data.monthlyStats || {})[monthStr]?.total || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedCategory}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedCategory] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(newsClicksRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'news_click_tracking',
      context: { newsId, newsTitle, newsCategory, newsUrl }
    });
  }
};

// Tracking des vues des bons plans (Deals)
export const trackDealsView = async (dealId, dealTitle, dealType) => {
  try {
    const dealsViewsRef = doc(db, 'analytics', 'dealsViews');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedType = sanitizeFirestoreKey(dealType || 'unknown');
    const sanitizedDealId = sanitizeFirestoreKey(dealId);

    await runTransaction(db, async (transaction) => {
      const viewsDoc = await transaction.get(dealsViewsRef);
      
      if (!viewsDoc.exists()) {
        transaction.set(dealsViewsRef, {
          total: 1,
          byType: { [sanitizedType]: 1 },
          byDeal: {
            [sanitizedDealId]: {
              title: dealTitle || 'Sans titre',
              views: 1
            }
          },
          dailyStats: {
            [dateStr]: {
              total: 1,
              [sanitizedType]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: 1,
              [sanitizedType]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = viewsDoc.data();
        const currentDealData = (data.byDeal || {})[sanitizedDealId];
        const currentViews = currentDealData?.views || 0;
        
        const updates = {
          total: (data.total || 0) + 1,
          [`byType.${sanitizedType}`]: ((data.byType || {})[sanitizedType] || 0) + 1,
          [`byDeal.${sanitizedDealId}`]: {
            title: dealTitle || 'Sans titre',
            views: currentViews + 1
          },
          [`dailyStats.${dateStr}.total`]: ((data.dailyStats || {})[dateStr]?.total || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedType}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedType] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: ((data.monthlyStats || {})[monthStr]?.total || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedType}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedType] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(dealsViewsRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'deals_view_tracking',
      context: { dealId, dealTitle, dealType }
    });
  }
};

// Tracking des clicks sur les bons plans
export const trackDealsClick = async (dealId, dealTitle, dealType, actionType, dealUrl) => {
  try {
    const dealsClicksRef = doc(db, 'analytics', 'dealsClicks');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedType = sanitizeFirestoreKey(dealType || 'unknown');
    const sanitizedDealId = sanitizeFirestoreKey(dealId);
    const sanitizedActionType = sanitizeFirestoreKey(actionType || 'unknown');

    await runTransaction(db, async (transaction) => {
      const clicksDoc = await transaction.get(dealsClicksRef);
      
      if (!clicksDoc.exists()) {
        transaction.set(dealsClicksRef, {
          total: 1,
          byType: { [sanitizedType]: 1 },
          byActionType: { [sanitizedActionType]: 1 },
          byDeal: {
            [sanitizedDealId]: {
              title: dealTitle || 'Sans titre',
              clicks: 1,
              lastActionType: actionType || 'unknown'
            }
          },
          dailyStats: {
            [dateStr]: {
              total: 1,
              [sanitizedType]: 1,
              [sanitizedActionType]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: 1,
              [sanitizedType]: 1,
              [sanitizedActionType]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = clicksDoc.data();
        const currentDealData = (data.byDeal || {})[sanitizedDealId];
        const currentClicks = currentDealData?.clicks || 0;
        
        const updates = {
          total: (data.total || 0) + 1,
          [`byType.${sanitizedType}`]: ((data.byType || {})[sanitizedType] || 0) + 1,
          [`byActionType.${sanitizedActionType}`]: ((data.byActionType || {})[sanitizedActionType] || 0) + 1,
          [`byDeal.${sanitizedDealId}`]: {
            title: dealTitle || 'Sans titre',
            clicks: currentClicks + 1,
            lastActionType: actionType || 'unknown'
          },
          [`dailyStats.${dateStr}.total`]: ((data.dailyStats || {})[dateStr]?.total || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedType}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedType] || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedActionType}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedActionType] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: ((data.monthlyStats || {})[monthStr]?.total || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedType}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedType] || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedActionType}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedActionType] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(dealsClicksRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'deals_click_tracking',
      context: { dealId, dealTitle, dealType, actionType, dealUrl }
    });
  }
};

// Tracking des intérêts pour les deals
export const trackDealInterest = async (dealId, dealTitle, dealType, action) => {
  try {
    const dealInterestsRef = doc(db, 'analytics', 'dealInterests');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedType = sanitizeFirestoreKey(dealType || 'unknown');
    const sanitizedDealId = sanitizeFirestoreKey(dealId);
    const sanitizedAction = sanitizeFirestoreKey(action || 'unknown');

    await runTransaction(db, async (transaction) => {
      const interestsDoc = await transaction.get(dealInterestsRef);
      
      if (!interestsDoc.exists()) {
        const initialInterests = action === 'add' ? 1 : 0;
        transaction.set(dealInterestsRef, {
          totalInterests: initialInterests,
          byType: { [sanitizedType]: initialInterests },
          byDeal: {
            [sanitizedDealId]: {
              title: dealTitle || 'Sans titre',
              interests: initialInterests
            }
          },
          byAction: {
            [sanitizedAction]: 1
          },
          dailyStats: {
            [dateStr]: {
              total: initialInterests,
              [sanitizedType]: initialInterests,
              [sanitizedAction]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: initialInterests,
              [sanitizedType]: initialInterests,
              [sanitizedAction]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = interestsDoc.data();
        const currentDealData = (data.byDeal || {})[sanitizedDealId];
        const currentInterests = currentDealData?.interests || 0;
        
        // Calculer le changement pour totalInterests
        const interestsDelta = action === 'add' ? 1 : action === 'remove' ? -1 : 0;
        const newInterests = Math.max(0, currentInterests + interestsDelta);
        
        const updates = {
          totalInterests: Math.max(0, (data.totalInterests || 0) + interestsDelta),
          [`byType.${sanitizedType}`]: Math.max(0, ((data.byType || {})[sanitizedType] || 0) + interestsDelta),
          [`byDeal.${sanitizedDealId}`]: {
            title: dealTitle || 'Sans titre',
            interests: newInterests
          },
          [`byAction.${sanitizedAction}`]: ((data.byAction || {})[sanitizedAction] || 0) + 1,
          [`dailyStats.${dateStr}.total`]: Math.max(0, ((data.dailyStats || {})[dateStr]?.total || 0) + interestsDelta),
          [`dailyStats.${dateStr}.${sanitizedType}`]: Math.max(0, ((data.dailyStats || {})[dateStr]?.[sanitizedType] || 0) + interestsDelta),
          [`dailyStats.${dateStr}.${sanitizedAction}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedAction] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: Math.max(0, ((data.monthlyStats || {})[monthStr]?.total || 0) + interestsDelta),
          [`monthlyStats.${monthStr}.${sanitizedType}`]: Math.max(0, ((data.monthlyStats || {})[monthStr]?.[sanitizedType] || 0) + interestsDelta),
          [`monthlyStats.${monthStr}.${sanitizedAction}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedAction] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(dealInterestsRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'deal_interest_tracking',
      context: { dealId, dealTitle, dealType, action }
    });
  }
};

// Tracking des ajouts au calendrier
export const trackCalendarAdd = async (dealId, dealTitle, dealType) => {
  try {
    const calendarAddsRef = doc(db, 'analytics', 'calendarAdds');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedType = sanitizeFirestoreKey(dealType || 'unknown');
    const sanitizedDealId = sanitizeFirestoreKey(dealId);

    await runTransaction(db, async (transaction) => {
      const addsDoc = await transaction.get(calendarAddsRef);
      
      if (!addsDoc.exists()) {
        transaction.set(calendarAddsRef, {
          total: 1,
          byType: { [sanitizedType]: 1 },
          byDeal: {
            [sanitizedDealId]: {
              title: dealTitle || 'Sans titre',
              adds: 1
            }
          },
          dailyStats: {
            [dateStr]: {
              total: 1,
              [sanitizedType]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: 1,
              [sanitizedType]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = addsDoc.data();
        const currentDealData = (data.byDeal || {})[sanitizedDealId];
        const currentAdds = currentDealData?.adds || 0;
        
        const updates = {
          total: (data.total || 0) + 1,
          [`byType.${sanitizedType}`]: ((data.byType || {})[sanitizedType] || 0) + 1,
          [`byDeal.${sanitizedDealId}`]: {
            title: dealTitle || 'Sans titre',
            adds: currentAdds + 1
          },
          [`dailyStats.${dateStr}.total`]: ((data.dailyStats || {})[dateStr]?.total || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedType}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedType] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: ((data.monthlyStats || {})[monthStr]?.total || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedType}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedType] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(calendarAddsRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'calendar_add_tracking',
      context: { dealId, dealTitle, dealType }
    });
  }
};

// Tracking des clicks sur le popup
export const trackPopupClick = async (popupVersion, ctaLink) => {
  try {
    const popupClicksRef = doc(db, 'analytics', 'popupClicks');
    const today = new Date();
    const { dateStr, monthStr } = formatDate(today);
    
    const sanitizedVersion = sanitizeFirestoreKey(popupVersion || 'unknown');
    // Sanitizer le lien pour créer une clé
    const linkKey = ctaLink ? sanitizeFirestoreKey(ctaLink) : 'none';

    await runTransaction(db, async (transaction) => {
      const clicksDoc = await transaction.get(popupClicksRef);
      
      if (!clicksDoc.exists()) {
        transaction.set(popupClicksRef, {
          total: 1,
          byVersion: { [sanitizedVersion]: 1 },
          byLink: {
            [linkKey]: {
              url: ctaLink || 'none',
              clicks: 1
            }
          },
          dailyStats: {
            [dateStr]: {
              total: 1,
              [sanitizedVersion]: 1
            }
          },
          monthlyStats: {
            [monthStr]: {
              total: 1,
              [sanitizedVersion]: 1
            }
          },
          lastUpdate: serverTimestamp()
        });
      } else {
        const data = clicksDoc.data();
        const currentLinkData = (data.byLink || {})[linkKey];
        const currentLinkClicks = currentLinkData?.clicks || 0;
        
        const updates = {
          total: (data.total || 0) + 1,
          [`byVersion.${sanitizedVersion}`]: ((data.byVersion || {})[sanitizedVersion] || 0) + 1,
          [`byLink.${linkKey}`]: {
            url: ctaLink || 'none',
            clicks: currentLinkClicks + 1
          },
          [`dailyStats.${dateStr}.total`]: ((data.dailyStats || {})[dateStr]?.total || 0) + 1,
          [`dailyStats.${dateStr}.${sanitizedVersion}`]: ((data.dailyStats || {})[dateStr]?.[sanitizedVersion] || 0) + 1,
          [`monthlyStats.${monthStr}.total`]: ((data.monthlyStats || {})[monthStr]?.total || 0) + 1,
          [`monthlyStats.${monthStr}.${sanitizedVersion}`]: ((data.monthlyStats || {})[monthStr]?.[sanitizedVersion] || 0) + 1,
          lastUpdate: serverTimestamp()
        };

        transaction.update(popupClicksRef, updates);
      }
    });
  } catch (error) {
    await trackError({
      error,
      type: 'popup_click_tracking',
      context: { popupVersion, ctaLink }
    });
  }
}; 